import React from 'react';

export const DEFAULT_SMA_OPTIONS = [1, 7, 30, 90, 180, 365];
export const DEFAULT_SMA_VALUE = 30;

type ParsedSmaConfig = {
  enabled: boolean;
  options?: number[];
  defaultValue?: number;
};

export function parseSmaConfig(rawSma: unknown): ParsedSmaConfig {
  if (typeof rawSma === 'boolean') {
    return { enabled: rawSma };
  }

  if (typeof rawSma === 'string') {
    return { enabled: rawSma.toLowerCase() !== 'false' };
  }

  if (rawSma && typeof rawSma === 'object' && !Array.isArray(rawSma)) {
    const maybeObject = rawSma as Record<string, unknown>;

    const rawValues = Array.isArray(maybeObject.Values)
      ? maybeObject.Values
      : Array.isArray(maybeObject.values)
        ? maybeObject.values
        : [];
    const parsedValues = (rawValues as unknown[]).map(value => Number(value)).filter(value => Number.isFinite(value));

    const rawDefault = maybeObject.DefaultValue ?? maybeObject.defaultValue;
    const parsedDefault = Number(rawDefault);
    const hasDefault = Number.isFinite(parsedDefault);

    if (parsedValues.length > 0) {
      const uniqueValues = Array.from(new Set(parsedValues));
      if (hasDefault && !uniqueValues.includes(parsedDefault)) {
        uniqueValues.unshift(parsedDefault);
      }
      return {
        enabled: true,
        options: uniqueValues,
        defaultValue: hasDefault ? parsedDefault : uniqueValues[0],
      };
    }

    if (hasDefault) {
      return {
        enabled: true,
        options: [parsedDefault],
        defaultValue: parsedDefault,
      };
    }

    return { enabled: false };
  }

  return { enabled: true };
}

export function useSmaState(rawSma: unknown) {
  const parsed = React.useMemo(() => parseSmaConfig(rawSma), [rawSma]);

  const smaOptions = React.useMemo(() => {
    if (parsed.options?.length) {
      return parsed.options;
    }
    return DEFAULT_SMA_OPTIONS;
  }, [parsed.options]);

  const resolvedDefault = React.useMemo(() => {
    if (parsed.defaultValue !== undefined) {
      return parsed.defaultValue;
    }
    if (smaOptions.includes(DEFAULT_SMA_VALUE)) {
      return DEFAULT_SMA_VALUE;
    }
    return smaOptions[0];
  }, [parsed.defaultValue, smaOptions]);

  const [smaEnabled, setSmaEnabled] = React.useState(parsed.enabled);
  const [smaValue, setSmaValue] = React.useState<number>(() => (parsed.enabled ? resolvedDefault : 1));

  React.useEffect(() => {
    setSmaEnabled(parsed.enabled && smaOptions.length > 0);
  }, [parsed.enabled, smaOptions.length]);

  React.useEffect(() => {
    if (!smaEnabled) {
      setSmaValue(1);
      return;
    }

    setSmaValue(prev => {
      const numericPrev = Number(prev);
      if (!smaOptions.includes(numericPrev)) {
        return resolvedDefault;
      }
      if (parsed.defaultValue !== undefined && numericPrev !== resolvedDefault) {
        return resolvedDefault;
      }
      if (numericPrev === 1 && resolvedDefault !== 1) {
        return resolvedDefault;
      }
      return numericPrev;
    });
  }, [smaEnabled, smaOptions, resolvedDefault, parsed.defaultValue]);

  return {
    smaOptions,
    smaValue,
    setSmaValue,
    smaEnabled,
  } as const;
}

export type UseSmaStateReturn = ReturnType<typeof useSmaState>;
